import type { IMappedGeoMappingItem } from 'partner360/components/filters'

import type { ICockpitAttentionDrillStep, TCockpitAttentionGrouping } from '../../model/types'

import type { IAttentionGroupingAccess } from './attentionGroupingAccess'
import { buildAttentionGeoNodes } from './buildAttentionGeoNodes'
import {
  buildAttentionOwnerNodes,
  UNASSIGNED_MANAGER_KEY,
  UNASSIGNED_MANAGER_LABEL,
  UNASSIGNED_PBM_KEY,
  UNASSIGNED_PBM_LABEL,
} from './buildAttentionOwnerNodes'
import { findAttentionGeoNode } from './findAttentionGeoNode'
import type { IAttentionGeoNode, IAttentionOwnerAssignment, IAttentionPartner } from './types'

export interface IResolveAttentionLevelParams {
  /** Partners behind the picked status. */
  partners: IAttentionPartner[]
  path: ICockpitAttentionDrillStep[]
  /** Grouping picked for the level the path ends on; `null` keeps that level's default. */
  grouping: TCockpitAttentionGrouping | null
  access: IAttentionGroupingAccess
  geoMappingItems: IMappedGeoMappingItem[]
  geoStartDepth: number
  pbmAssignments: IAttentionOwnerAssignment[]
  managerAssignments: IAttentionOwnerAssignment[]
}

export interface IAttentionLevelStep {
  step: ICockpitAttentionDrillStep
  node: IAttentionGeoNode
}

export interface IResolvedAttentionLevel {
  /** Steps of the path that still address a group, outermost first. */
  steps: IAttentionLevelStep[]
  /** Partners the level stands on: the status' own, narrowed by every step. */
  partners: IAttentionPartner[]
  grouping: TCockpitAttentionGrouping
  /** Groupings the reader may switch this level to. */
  options: TCockpitAttentionGrouping[]
  /** Groups the level lists; empty when it lists the partners themselves. */
  nodes: IAttentionGeoNode[]
}

type TGeoParams = Pick<IResolveAttentionLevelParams, 'geoMappingItems' | 'geoStartDepth'>

/** The geo subtree under `geoId`, or the whole tree before any geo step was taken. */
const getGeoBase = (partners: IAttentionPartner[], geoId: string | null, geo: TGeoParams) => {
  const roots = buildAttentionGeoNodes({
    partners,
    geoMappingItems: geo.geoMappingItems,
    startDepth: geo.geoStartDepth,
  })

  if (!geoId) {
    return roots
  }

  return findAttentionGeoNode(roots, geoId)?.children ?? []
}

/** A geo level of one node is not a choice, so the walk steps through it to what it holds. */
const skipSingleGeoNodes = (nodes: IAttentionGeoNode[]) => {
  let level = nodes

  while (level.length === 1) {
    level = level[0].children
  }

  return level
}

const buildOwnerLevel = (
  grouping: 'pbm' | 'manager',
  partners: IAttentionPartner[],
  params: IResolveAttentionLevelParams
) =>
  grouping === 'pbm'
    ? buildAttentionOwnerNodes({
        partners,
        assignments: params.pbmAssignments,
        unassigned: { itemId: UNASSIGNED_PBM_KEY, name: UNASSIGNED_PBM_LABEL },
      })
    : buildAttentionOwnerNodes({
        partners,
        assignments: params.managerAssignments,
        unassigned: { itemId: UNASSIGNED_MANAGER_KEY, name: UNASSIGNED_MANAGER_LABEL },
      })

const findStepNode = (
  { grouping, itemId }: ICockpitAttentionDrillStep,
  partners: IAttentionPartner[],
  geoId: string | null,
  params: IResolveAttentionLevelParams
): IAttentionGeoNode | null => {
  if (grouping === 'market') {
    return findAttentionGeoNode(getGeoBase(partners, geoId, params), itemId)
  }

  if (grouping === 'partner') {
    return null
  }

  return buildOwnerLevel(grouping, partners, params).find((node) => node.itemId === itemId) ?? null
}

/**
 * The grouping a level is shown in: the reader's pick where the level offers it, else the level's
 * own default. A geo step opens the next geo level and an owner opens their partners; the first
 * level takes the role's default, or the first grouping it still offers.
 */
const pickGrouping = (
  requested: TCockpitAttentionGrouping | null,
  options: TCockpitAttentionGrouping[],
  access: IAttentionGroupingAccess,
  lastStep?: ICockpitAttentionDrillStep
): TCockpitAttentionGrouping => {
  const levelDefault = lastStep?.grouping === 'market' ? 'market' : 'partner'
  const wanted = requested ?? (lastStep ? levelDefault : access.defaultGrouping)

  if (options.includes(wanted)) {
    return wanted
  }

  if (lastStep) {
    return 'partner'
  }

  if (options.includes(access.defaultGrouping)) {
    return access.defaultGrouping
  }

  return options.find((option) => option !== 'partner') ?? 'partner'
}

/**
 * Where the attention drill-down stands: the groups it walked into, the partners left under them,
 * and how the level they open is grouped.
 *
 * Every level is grouped on its own. A path can walk a market, then a PBM inside it, and list that
 * PBM's partners by country: each step narrows the partners the next one is built from. Geo steps
 * continue the tree from the deepest place already walked; an owner grouping is offered once per
 * path, since a second step by the same owner would list one group.
 *
 * A step the data no longer holds ends the path there, so a filter change keeps the reader as deep
 * as the new partners allow.
 */
export const resolveAttentionLevel = (
  params: IResolveAttentionLevelParams
): IResolvedAttentionLevel => {
  const { path, access } = params
  const steps: IAttentionLevelStep[] = []
  let partners = params.partners
  let geoId: string | null = null

  for (const step of path) {
    const node = findStepNode(step, partners, geoId, params)

    if (!node) {
      break
    }

    steps.push({ step, node })

    const nodeIds = new Set(node.partnerIds)
    partners = partners.filter(({ partnerId }) => nodeIds.has(partnerId))

    if (step.grouping === 'market') {
      geoId = node.itemId
    }
  }

  const geoNodes = skipSingleGeoNodes(getGeoBase(partners, geoId, params))
  const walked = new Set(steps.map(({ step }) => step.grouping))
  const options = access.options.filter((option) => {
    if (option === 'market') {
      return geoNodes.length > 0
    }

    return option === 'partner' || !walked.has(option)
  })

  const grouping = pickGrouping(params.grouping, options, access, steps[steps.length - 1]?.step)

  let nodes: IAttentionGeoNode[] = []

  if (grouping === 'market') {
    nodes = geoNodes
  } else if (grouping !== 'partner') {
    nodes = buildOwnerLevel(grouping, partners, params)
  }

  return { steps, partners, grouping, options, nodes }
}
